import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Send, Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;


export default function AskNaviPanel({ open, onOpenChange, programmeId, module, period, token }) {
  const scope = period ? 'week' : 'module';
  const subject = period ? period.title : module.title;
  const endpoint = period ? `${API}/programmes/${programmeId}/periods/${period.period_id}/ask-navi` : `${API}/programmes/${programmeId}/modules/${module.module_id}/ask-navi`;
  const STARTERS = [`Summarise this ${scope} in plain language`, 'Give me a workplace example', `Quiz me with 3 questions on this ${scope}`];
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const ask = async (text) => {
    const message = (text ?? input).trim();
    if (!message || loading) return;
    setInput('');
    const history = messages;
    setMessages((m) => [...m, { role: 'user', content: message }]);
    setLoading(true);
    try {
      const res = await axios.post(endpoint, { message, history },
        { headers: { Authorization: `Bearer ${token}` } });
      setMessages((m) => [...m, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'NAVI is unavailable right now');
      setMessages((m) => m.slice(0, -1));
      setInput(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-zinc-950 border-zinc-800 w-full sm:max-w-md flex flex-col p-0" data-testid="ask-navi-panel">
        <SheetHeader className="p-5 border-b border-zinc-800 text-left">
          <SheetTitle className="text-white flex items-center gap-2"><Sparkles className="h-5 w-5 text-navigate-gold" />Ask NAVI</SheetTitle>
          <SheetDescription className="text-zinc-400">Ask NAVI anything about {subject} — concepts, examples, or how it applies to your work.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {messages.length === 0 && (
            <div className="space-y-2">
              {STARTERS.map((s) => (
                <button key={s} type="button" onClick={() => ask(s)} className="block w-full text-left text-sm px-3 py-2 rounded-lg border border-zinc-800 text-zinc-300 hover:border-navigate-gold/50 hover:text-white transition-colors" data-testid={`ask-navi-starter-${STARTERS.indexOf(s)}`}>{s}</button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'ml-auto bg-teal-600 text-white' : 'bg-zinc-900 border border-zinc-800 text-zinc-200'}`} data-testid={`ask-navi-msg-${i}`}>{m.content}</div>
          ))}
          {loading && <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />NAVI is thinking…</div>}
          <div ref={endRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="p-4 border-t border-zinc-800 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask NAVI anything…" maxLength={2000}
            className="flex-1 rounded-full bg-zinc-900 border border-zinc-800 px-4 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-teal-500" data-testid="ask-navi-input" />
          <button type="submit" disabled={loading || !input.trim()} className="h-10 w-10 rounded-full bg-teal-600 hover:bg-teal-500 disabled:opacity-40 flex items-center justify-center text-white transition-colors" data-testid="ask-navi-send-btn">
            <Send className="h-4 w-4" />
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
